/**
 * # Homepage Slide Animation
 */

/**
 * ## Imports
 */
import './HomepageSlideAnimation.scss';

import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import ButtonCtaRebrand from '../../../common/components/ButtonCtaRebrand/ButtonCtaRebrand';

import history from '../../../app/history';
import { analyticsType } from '../../../constants';
import { action, category } from '../../../constants/metrics';
import HomepageSlideFootball from './HomepageSlideFootball/HomepageSlideFootball';
import HomepageSlideNavigation from './HomepageSlideNavigation/HomepageSlideNavigation';
import HomepageSlideTimezone from './HomepageSlideTimezone/HomepageSlideTimezone';
import HomepageSlideWeather from './HomepageSlideWeather/HomepageSlideWeather';

/**
 * ## Component
 * @param {string} id
 * @returns React
 */

const HomepageSlideAnimation = ({ id, isSearchPage, submitMetrics }) => {
    const { t } = useTranslation('websiteV2');
    /**
     * ### State
     */
    const [activeSlider, setActiveSlider] = useState(0);
    const [startX, setStartX] = useState(null);
    const [isSwiped, setIsSwiped] = useState(false);
    const [intervalId, setIntervalId] = useState(0);

    const slideCount = 3;
    const slideNames = [
        t('homepage-section-3-infocard-subtitle-weather', 'Weather infocard'),
        t('homepage-section-3-infocard-subtitle-sports', 'Soccer Infocard'),
        t('homepage-section-3-infocard-subtitle-timezone', 'Timezone infocard'),
    ];
    let SLIDER_DELAY = 4000;

    function handleTouchStart(event) {
        setStartX(event.touches[0].clientX);
    }

    function handleTouchMove(event) {
        const currentX = event.touches[0].clientX;
        const deltaX = startX - currentX;

        if (!isSwiped) {
            if (deltaX > 150) {
                // Swipe left
                setActiveSlider((prevState) => (prevState === 2 ? 0 : prevState + 1));
                setIsSwiped(true);
            } else if (deltaX < -150) {
                // Swipe right
                setActiveSlider((prevState) => (prevState === 0 ? 2 : prevState - 1));
                setIsSwiped(true);
            }
        }
    }

    function handleTouchEnd() {
        setIsSwiped(false);
        setStartX(null);
        handleStopInterval();
    }

    const clickHandler = (item) => {
        setActiveSlider(item);
        handleStopInterval();
    };

    const handleStopInterval = () => {
        clearInterval(intervalId);
        setIntervalId(null);
    };

    useEffect(() => {
        /* - Todo: we need to reset this interval inside `clickHandler`. */
        const interval = setInterval(() => {
            setActiveSlider((active) => {
                return active < slideCount - 1 ? active + 1 : 0;
            });
        }, SLIDER_DELAY);
        setIntervalId(interval);

        /**
         * Clean up
         */
        return () => {
            clearInterval(interval);
        };
    }, [setActiveSlider, SLIDER_DELAY]);

    const handleViewMore = () => {
        let redirectUrl = '/';
        if (!isSearchPage) {
            redirectUrl = '/tempest-search';
        }
        submitMetrics(
            {
                Category: category.homepage,
                Action: action.click,
                Object: 'searchViewMore',
                Value: !isSearchPage ? 'homepage_infocards' : 'searchpage_infocards',
            },
            analyticsType.wb,
        );
        history.push(redirectUrl);
    };

    return (
        <section className="slide-container" id={id}>
            <div className="home-centerwrapper">
                <div className="slide-container__row">
                    <figure
                        className="slide-container__row-left"
                        id="slider-1"
                        onTouchEnd={handleTouchEnd}
                        onTouchMove={handleTouchMove}
                        onTouchStart={handleTouchStart}
                    >
                        <figcaption className="sr-only">Tempest search widgets overview.</figcaption>
                        {/*
                         * In order to add slides we just have to add a new list item inside `.slide-container__nav`.
                         * This new item's `href` attribute than just has to match the respective `id` inside
                         * `.slide-container__list`.
                         */}
                        <ul className="slide-container__list">
                            <li
                                className={`slide-container__list-item ${
                                    activeSlider === 0
                                        ? 'slide-container__list-item__weather slide-container__list-item--visible'
                                        : ''
                                }`}
                                id="slide-container-weather"
                            >
                                <HomepageSlideWeather />
                            </li>
                            <li
                                className={`slide-container__list-item ${
                                    activeSlider === 1
                                        ? 'slide-container__list-item__football slide-container__list-item--visible'
                                        : ''
                                }`}
                                id="slide-container-football"
                            >
                                <HomepageSlideFootball />
                            </li>
                            <li
                                className={`slide-container__list-item ${
                                    activeSlider === 2
                                        ? 'slide-container__list-item__football slide-container__list-item--visible'
                                        : ''
                                }`}
                                id="slide-container-timezone"
                            >
                                <HomepageSlideTimezone />
                            </li>
                        </ul>
                        <HomepageSlideNavigation
                            slideCount={slideCount}
                            activeSlider={activeSlider}
                            onClick={clickHandler}
                            label={slideNames[activeSlider]}
                        />
                    </figure>
                    <div className="slide-container__row-right">
                        <header>
                            <h2 className="slide-container__subheadline">
                                <Trans
                                    ns="websiteV2"
                                    i18nKey="homepage-section-3-title"
                                    defaults="Find the stuff you love in seconds flat."
                                    components={{ br: <br /> }}
                                />
                            </h2>
                        </header>
                        <p className="slide-container__paragraph">
                            {t(
                                'homepage-section-3-paragraph',
                                'Whether it’s sports, movies, weather, or more, our infocards deliver your world at-a-glance. So you can get in, get out, and get on with life.',
                            )}
                        </p>
                        <ButtonCtaRebrand
                            label={t('homepage-section-3-infocard-action', 'View more')}
                            classList="slide-container__button"
                            layoutBig={true}
                            layoutReduced={true}
                            arrowIndicator={true}
                            onClick={handleViewMore}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HomepageSlideAnimation;
